import { axiosInstance as api } from "@/services/axios-instance";

// Pagination types
export interface PaginationParams {
  currentPage: number;
  pageSize: number;
  q?: string; // search query
  s?: string; // sort query
}

export interface CursorPaginationParams {
  before?: number;
  after?: number;
  pageSize: number;
  q?: string;
}

export interface CurrencyPaginationParams extends PaginationParams {
  based: "USD";
  to?: string;
  session?: string;
}

export interface PaginationMeta {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface CursorPaginationMeta {
  hasNext: true;
  hasPrevious: false;
  nextCursor: number;
  pageSize: number;
  prevCursor: number;
  totalItems: number;
}
export interface ApiResponse<T> {
  status: string;
  statusCode: number;
  version: string;
  type: string;
  timestamp: string;
  message: string;
  signature: string;
  payload: T;
  pagination?: PaginationMeta;
}

export interface Response<T> {
  status: string;
  statusCode: number;
  version: string;
  type: string;
  timestamp: string;
  message: string;
  signature: string;
  payload: T;
}

export interface CursorApiResponse<T>
  extends Omit<ApiResponse<T>, "pagination"> {
  pagination?: CursorPaginationMeta;
}

// Simple API functions
export const fetchData = async <T>(
  endpoint: string,
  params?: Record<string, any>,
): Promise<T> => {
  const response = await api.get<T>(endpoint, { params });
  return response.data;
};

export const createData = async <T>(
  endpoint: string,
  data?: Record<string, any>,
): Promise<T> => {
  const response = await api.post<T>(endpoint, data);
  return response.data;
};

export const updateData = async <T>(
  endpoint: string,
  id: string | number,
  data: Record<string, any>,
): Promise<T> => {
  const response = await api.put<T>(`${endpoint}/${id}`, data);
  return response.data;
};

export const deleteData = async (
  endpoint: string,
  id: string | number,
): Promise<void> => {
  await api.delete(`${endpoint}/${id}`);
};

// Pagination helper
export const fetchPaginated = async <
  T,
  P extends PaginationParams = PaginationParams,
>(
  endpoint: string,
  params: P,
): Promise<ApiResponse<T>> => {
  const response = await api.get<ApiResponse<T>>(endpoint, { params });
  return response.data;
};

export const fetchCursorPaginated = async <
  T,
  P extends CursorPaginationParams = CursorPaginationParams,
>(
  endpoint: string,
  params: P,
): Promise<CursorApiResponse<T>> => {
  const response = await api.get<CursorApiResponse<T>>(endpoint, { params });
  return response.data;
};

type DashboardParams = {
  username: string;
  from: string;
  to: string;
};
export const fetchDashboardData = async <T>(
  endpoint: string,
  params: DashboardParams,
): Promise<Response<T>> => {
  const response = await api.get<Response<T>>(endpoint, {
    params: { ...params },
  });
  return response.data;
};
